import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  FileText,
  MoreVertical,
  Eye,
  Edit,
  Trash2,
  Send,
  Search,
  Filter,
  Download,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  TrendingUp,
  Calendar,
  PenTool,
  RefreshCw,
} from 'lucide-react';
import { useSite } from '@/contexts/SiteContext';
import { authFetch } from '@/utils/auth-fetch';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import { AIWriterPreview } from '@/components/ai-writer/AIWriterPreview';

interface Article {
  id: string;
  title: string;
  slug: string;
  content: string;
  content_html: string;
  meta_description: string;
  word_count: number;
  status: 'draft' | 'review' | 'published' | 'archived';
  seo_score?: number;
  keyword_density: any;
  topic_name?: string;
  created_at: string;
  published_at?: string;
  generated_at?: string;
}

export function AIArticles() {
  const { selectedSite } = useSite();
  const [articles, setArticles] = useState<Article[]>([]);
  const [filteredArticles, setFilteredArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    if (selectedSite?.id) {
      fetchArticles();
    }
  }, [selectedSite]);

  useEffect(() => {
    filterArticles();
  }, [articles, searchTerm, statusFilter]);

  const fetchArticles = async () => {
    if (!selectedSite) return;

    setLoading(true);
    try {
      const response = await authFetch(
        `/api/ai-writer/articles?siteId=${selectedSite.id || selectedSite._id}`
      );
      const data = await response.json();
      // Handle error responses and ensure we always have an array
      if (Array.isArray(data)) {
        setArticles(data);
      } else if (data.error) {
        console.error('Error from API:', data.error);
        toast.error(data.error);
        setArticles([]);
      } else {
        setArticles([]);
      }
    } catch (error) {
      console.error('Error fetching AI articles:', error);
      toast.error('Failed to load AI articles');
      setArticles([]);
    } finally {
      setLoading(false);
    }
  };

  const filterArticles = () => {
    let filtered = [...articles];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (article) =>
          article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          article.topic_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter((article) => article.status === statusFilter);
    }

    setFilteredArticles(filtered);
  };

  const handleStatusChange = async (articleId: string, newStatus: string) => {
    try {
      const response = await authFetch(`/api/ai-writer/articles/${articleId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });

      if (!response.ok) {
        throw new Error('Failed to update status');
      }

      toast.success(`Article status updated to ${newStatus}`);
      fetchArticles();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update article status');
    }
  };

  const handlePublish = async (articleId: string) => {
    try {
      const response = await authFetch(`/api/ai-writer/articles/${articleId}/publish`, {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error('Failed to publish');
      }

      toast.success('Article published successfully!');
      fetchArticles();
    } catch (error) {
      console.error('Error publishing article:', error);
      toast.error('Failed to publish article');
    }
  };

  const handleDelete = async (articleId: string) => {
    if (!confirm('Are you sure you want to delete this article?')) return;

    try {
      const response = await authFetch(`/api/ai-writer/articles/${articleId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete');
      }

      toast.success('Article deleted');
      fetchArticles();
    } catch (error) {
      console.error('Error deleting article:', error);
      toast.error('Failed to delete article');
    }
  };

  const exportArticle = (article: Article) => {
    const blob = new Blob([article.content_html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${article.slug}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Article exported');
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'draft':
        return <Clock className="h-4 w-4" />;
      case 'review':
        return <AlertCircle className="h-4 w-4" />;
      case 'published':
        return <CheckCircle className="h-4 w-4" />;
      case 'archived':
        return <XCircle className="h-4 w-4" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return 'secondary';
      case 'review':
        return 'warning';
      case 'published':
        return 'success';
      case 'archived':
        return 'outline';
      default:
        return 'default';
    }
  };

  const stats = {
    total: articles.length,
    draft: articles.filter((a) => a.status === 'draft').length,
    review: articles.filter((a) => a.status === 'review').length,
    published: articles.filter((a) => a.status === 'published').length,
  };

  if (!selectedSite) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>Please select a site to view AI-generated articles.</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <PenTool className="h-8 w-8" />
          AI Generated Articles
        </h1>
        <p className="text-muted-foreground mt-2">
          Manage AI-generated blog articles created from researched topics
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Total Articles</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Draft</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">{stats.draft}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">In Review</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{stats.review}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Published</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats.published}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Articles</CardTitle>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search articles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8 w-64"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="review">In Review</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={fetchArticles}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading articles...</div>
          ) : filteredArticles.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {articles.length === 0
                ? 'No AI-generated articles yet. Use the AI Writer from the "The Sector" page to create articles.'
                : 'No articles match your filters.'}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Topic</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>SEO Score</TableHead>
                  <TableHead>Word Count</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredArticles.map((article) => (
                  <TableRow key={article.id}>
                    <TableCell className="font-medium max-w-xs truncate">
                      {article.title}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {article.topic_name || 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={getStatusColor(article.status) as any} className="flex items-center gap-1 w-fit">
                        {getStatusIcon(article.status)}
                        {article.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {article.seo_score ? (
                        <div className="flex items-center gap-1">
                          <TrendingUp className="h-3 w-3" />
                          <span className={article.seo_score >= 80 ? 'text-green-600' : article.seo_score >= 60 ? 'text-yellow-600' : 'text-red-600'}>
                            {article.seo_score}%
                          </span>
                        </div>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>{article.word_count}</TableCell>
                    <TableCell className="text-sm">
                      <div>{format(new Date(article.created_at), 'MMM d, yyyy')}</div>
                      <div className="text-xs text-muted-foreground">{format(new Date(article.created_at), 'HH:mm:ss')}</div>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => {
                              setSelectedArticle(article);
                              setPreviewOpen(true);
                            }}
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            Preview
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => exportArticle(article)}
                          >
                            <Download className="h-4 w-4 mr-2" />
                            Export HTML
                          </DropdownMenuItem>
                          {article.status === 'draft' && (
                            <>
                              <DropdownMenuItem
                                onClick={() => handleStatusChange(article.id, 'review')}
                              >
                                <AlertCircle className="h-4 w-4 mr-2" />
                                Send to Review
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handlePublish(article.id)}
                              >
                                <Send className="h-4 w-4 mr-2" />
                                Publish Now
                              </DropdownMenuItem>
                            </>
                          )}
                          {article.status === 'review' && (
                            <>
                              <DropdownMenuItem
                                onClick={() => handleStatusChange(article.id, 'draft')}
                              >
                                <Edit className="h-4 w-4 mr-2" />
                                Back to Draft
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handlePublish(article.id)}
                              >
                                <Send className="h-4 w-4 mr-2" />
                                Approve & Publish
                              </DropdownMenuItem>
                            </>
                          )}
                          {article.status === 'published' && (
                            <DropdownMenuItem
                              onClick={() => handleStatusChange(article.id, 'archived')}
                            >
                              <XCircle className="h-4 w-4 mr-2" />
                              Archive
                            </DropdownMenuItem>
                          )}
                          {article.status === 'archived' && (
                            <DropdownMenuItem
                              onClick={() => handleStatusChange(article.id, 'draft')}
                            >
                              <Clock className="h-4 w-4 mr-2" />
                              Restore to Draft
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            onClick={() => handleDelete(article.id)}
                            className="text-red-600"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Article Preview Modal */}
      {selectedArticle && (
        <AIWriterPreview
          open={previewOpen}
          onOpenChange={(open) => {
            setPreviewOpen(open);
            if (!open) setSelectedArticle(null);
          }}
          article={selectedArticle}
          onUpdate={(updated) => {
            setSelectedArticle(updated);
            fetchArticles();
          }}
        />
      )}
    </div>
  );
}